"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "motion/react";
import { HiX } from "react-icons/hi";
import { Chat } from "@/components/chat";
import { useChatData } from "@/components/chat/hooks";
import { GLOBAL_CHAT_CONSTANTS } from "@/components/global-chat/chat-constants";
import { useCommunication } from "@/contexts/chat-provider";
import { formatName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ChatWindowState } from "@/types/chat";
import { ChatStatus } from "@/types/enums/chat";
interface ChatWindowGlobalWrapperProps {
  windowProps: ChatWindowState;
}

// Parent only manages layout/animation state - no data fetching
export default function ChatWindowGlobalWrapper(t0) {
  const $ = _c(5);
  const {
    windowProps
  } = t0;
  const {
    closeChat,
    maximizeChat,
    minimizeChat
  } = useCommunication();
  let t1;
  if ($[0] !== closeChat || $[1] !== maximizeChat || $[2] !== minimizeChat || $[3] !== windowProps) {
    t1 = <AnimatePresence mode="wait">{windowProps.isMinimized ? <MinimizedChatPreview chatId={windowProps.chatId} onMaximize={() => maximizeChat(windowProps)} onClose={() => closeChat(windowProps)} /> : <motion.div key={`${windowProps.chatId}-maximized`} style={{
        width: GLOBAL_CHAT_CONSTANTS.CHAT_WIDTH,
        height: GLOBAL_CHAT_CONSTANTS.CHAT_HEIGHT,
        opacity: 1
      }} exit={{
        translateY: GLOBAL_CHAT_CONSTANTS.CHAT_HEIGHT,
        opacity: 2
      }} transition={{
        duration: 0.2,
        ease: "easeInOut"
      }} className="motion-preset-slide-up-md relative z-10 flex flex-col overflow-hidden rounded-t-2xl bg-background/40 shadow-lg backdrop-blur-lg motion-duration-200"><div className="min-h-0 flex-1 overflow-hidden"><Chat chatId={windowProps.chatId} size="sm" showSuggestions={true} onMinimize={() => minimizeChat(windowProps)} onClose={() => closeChat(windowProps)}><div className="relative flex h-full w-full flex-col"><Chat.Header /><Chat.Messages /><Chat.Input /><Chat.Modals /></div></Chat></div></motion.div>}</AnimatePresence>;
    $[0] = closeChat;
    $[1] = maximizeChat;
    $[2] = minimizeChat;
    $[3] = windowProps;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
interface MinimizedChatPreviewProps {
  chatId: string | undefined;
  onMaximize: () => void;
  onClose: () => void;
}
function MinimizedChatPreview(t0) {
  const $ = _c(34);
  const {
    chatId,
    onMaximize,
    onClose
  } = t0;
  let t1;
  if ($[0] !== chatId) {
    t1 = {
      chatId
    };
    $[0] = chatId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    chat
  } = useChatData(t1);
  const isLoading = !chat && !!chatId;
  const t2 = `${chatId}-minimized`;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      translateY: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT / 2,
      opacity: 0
    };
    t4 = {
      width: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_WIDTH,
      height: GLOBAL_CHAT_CONSTANTS.MINIMIZED_CHAT_HEIGHT,
      opacity: 1
    };
    t5 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  const t6 = chat?.status === ChatStatus.UNREAD ? "bg-primary" : "bg-background/80 backdrop-blur-lg";
  let t7;
  if ($[5] !== t6) {
    t7 = cx("motion-preset-slide-up-md relative z-10 flex cursor-pointer flex-row items-center justify-between rounded-t-2xl border px-2 shadow-lg motion-duration-200", t6);
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== chat?.status) {
    t8 = chat?.status === ChatStatus.UNREAD && <span className="flex h-2 w-2 flex-shrink-0 rounded-full bg-status-error-text" />;
    $[7] = chat?.status;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = chat?.status === ChatStatus.UNREAD ? "text-gray-100" : "text-foreground";
  let t10;
  if ($[9] !== t9) {
    t10 = cx("min-w-0 truncate text-body-emphasis", t9);
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== chat?.patients || $[12] !== isLoading) {
    t11 = isLoading ? <div className="h-4 w-36 animate-pulse rounded-full bg-muted" /> : formatName(chat?.patients[0]?.firstName, chat?.patients[0]?.lastName);
    $[11] = chat?.patients;
    $[12] = isLoading;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t8) {
    t13 = <div className="ml-1 mr-1 flex min-w-0 flex-1 flex-row items-center gap-1.5 overflow-hidden">{t8}{t12}</div>;
    $[17] = t12;
    $[18] = t8;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = chat?.status === ChatStatus.UNREAD ? "text-gray-100 hover:bg-muted hover:text-foreground-secondary" : "text-foreground hover:text-foreground-secondary";
  let t15;
  if ($[20] !== t14) {
    t15 = cx("p-1", t14);
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== onClose) {
    t16 = e => {
      e.stopPropagation();
      onClose();
    };
    $[22] = onClose;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <HiX className="h-4 w-4" />;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t15 || $[26] !== t16) {
    t18 = <button className={t15} onClick={t16}>{t17}</button>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== onMaximize || $[29] !== t13 || $[30] !== t18 || $[31] !== t2 || $[32] !== t7) {
    t19 = <motion.div key={t2} initial={false} exit={t3} style={t4} transition={t5} className={t7} onClick={onMaximize}>{t13}{t18}</motion.div>;
    $[28] = onMaximize;
    $[29] = t13;
    $[30] = t18;
    $[31] = t2;
    $[32] = t7;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
}